/**
 * B117 slice 3 — display primitives. Money renders the FACT ("$1,234.56"),
 * never the schema ("0.00 USD" — learned-check #5); the amount stays
 * integer minor units end to end, formatted by pure string arithmetic
 * (money.ts) with Intl consulted only for the symbol, its position, and
 * the currency's decimal count — never for the value. DateTimeText
 * renders an instant in the tenant's zone in human words; no ISO string
 * ever reaches an operator's eyes (property-pinned in the tests).
 * ConfirmationCode is B115's chip: the one identifier a human may see.
 */
import { useState, type ReactNode } from "react";
import { Banner, Button } from "./components.js";
import { minorToDisplay } from "./money.js";
import { codeCopyWords, copyCodeEffect, type ClipboardLike, type CodeCopyOutcome } from "./copy.js";

/* -------------------------------------------------------------- Money */

interface CurrencyShape {
  readonly symbol: string;
  readonly suffix: boolean;
  readonly decimals: number;
}

function currencyShape(currency: string): CurrencyShape {
  try {
    const format = new Intl.NumberFormat("en-US", { style: "currency", currency });
    const parts = format.formatToParts(1);
    const symbol = parts.find((p) => p.type === "currency")?.value ?? currency;
    const suffix = parts.findIndex((p) => p.type === "currency") > parts.findIndex((p) => p.type === "integer");
    const decimals = format.resolvedOptions().maximumFractionDigits ?? 2;
    return { symbol, suffix, decimals };
  } catch {
    return { symbol: currency, suffix: false, decimals: 2 };
  }
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Format integer minor units for humans. Pure string math on the value
 * (minorToDisplay); Intl supplies only symbol, position and decimals.
 */
export function formatMoney(minor: number, currency: string, decimals?: number): string {
  const shape = currencyShape(currency);
  const scale = decimals ?? shape.decimals;
  const plain = minorToDisplay(minor, scale); // "-1234.56"
  const negative = plain.startsWith("-");
  const bare = negative ? plain.slice(1) : plain;
  const [whole = "0", frac] = bare.split(".");
  const body = groupThousands(whole) + (frac === undefined ? "" : `.${frac}`);
  const withSymbol = shape.suffix ? `${body} ${shape.symbol}` : `${shape.symbol}${body}`;
  return negative ? `-${withSymbol}` : withSymbol;
}

/**
 * B187 — money with the tenant fallback, ONE mint: the row's own currency
 * wins, the tenant's homeCurrency covers the zero-rows case (B176), and
 * bare minor-display is the honest last resort when neither exists —
 * a face may state a bare figure honestly, but never because a helper
 * copy drifted (home.tsx was the third copy of this exact ternary).
 */
export function moneyWords(minor: number, currency?: string, fallbackCurrency?: string): string {
  const c = currency ?? fallbackCurrency;
  return c === undefined ? minorToDisplay(minor) : formatMoney(minor, c);
}

/**
 * B542 (332:49) — the rail's COMPACT money register: "$48.2k", "$1.2M",
 * "$980", zero as "$0". Display-only rounding of an already-integer minor
 * amount (the value itself never leaves integer math); one decimal at k/M,
 * the ".0" trimmed. Never for a figure an operator acts on exactly — a
 * rail glances, a table states.
 */
export function formatMoneyCompact(minor: number, currency: string): string {
  const shape = currencyShape(currency);
  const negative = minor < 0;
  const major = Math.round(Math.abs(minor) / 10 ** shape.decimals);
  const oneDecimal = (n: number): string => (n * 10 % 10 === 0 ? String(Math.round(n)) : n.toFixed(1));
  const body =
    major >= 1_000_000
      ? `${oneDecimal(Math.round(major / 100_000) / 10)}M`
      : major >= 1000
        ? `${oneDecimal(Math.round(major / 100) / 10)}k`
        : String(major);
  const withSymbol = shape.suffix ? `${body} ${shape.symbol}` : `${shape.symbol}${body}`;
  return negative ? `-${withSymbol}` : withSymbol;
}

/** The display half of MoneyInput's contract: "$1,234.56", never "1234.56 USD". */
export function Money(props: { minor: number; currency: string; decimals?: number }): ReactNode {
  return (
    <span className="t-money-text">{formatMoney(props.minor, props.currency, props.decimals)}</span>
  );
}

/* ------------------------------------------------------- DateTimeText */

/**
 * "Mon 3 Aug · 14:00" in the tenant's zone — the platform's 24h register
 * (B515: every frame speaks an h23 clock; the 12h voice this component
 * carried was the last one in the fleet). Deliberately renders NO
 * dateTime attribute: the component's contract is that no ISO substring
 * leaves it, attribute or text — the wall the ticket names.
 */
export function dateTimeWords(epochMs: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(epochMs));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")} · ${get("hour")}:${get("minute")}`;
}

export function DateTimeText(props: { epochMs: number; timeZone: string }): ReactNode {
  return <time className="t-datetime-text">{dateTimeWords(props.epochMs, props.timeZone)}</time>;
}

/* ------------------------------------------------------- ColorSwatch */

/**
 * B122 — a stored color as a FACT: a chip painting the actual value with
 * the hex readable beside it. For reviewing configured colors (channel
 * branding); the inline background is data, not styling — the same
 * stance as chart segment widths. Never a way to theme a surface.
 */
export function ColorSwatch(props: { hex: string; label?: string }): ReactNode {
  return (
    <span className="t-colorswatch" role="img" aria-label={`${props.label ?? "Color"}: ${props.hex}`}>
      <span className="t-colorswatch-chip" style={{ background: props.hex }} aria-hidden="true" />
      <code className="t-colorswatch-hex">{props.hex}</code>
    </span>
  );
}

/* --------------------------------------------------- ConfirmationCode */

// B551 — the copy machinery moved to the leaf `copy.ts` so `Banner`'s
// ReferenceTag (components.tsx) can share it without an import cycle.
export { codeCopyWords, copyCodeEffect, type ClipboardLike, type CodeCopyOutcome } from "./copy.js";

/**
 * The face: pure, so each outcome can be rendered and pinned without a
 * browser (the container/face split OneWayDoorFace and the admin's
 * onboarding panels already use).
 */
export function ConfirmationCodeFace(props: {
  code: string;
  outcome: CodeCopyOutcome;
  onCopy: () => void;
}): ReactNode {
  const copied = props.outcome === "copied";
  return (
    <span className="t-code">
      <code className="t-code-text">{props.code}</code>
      <button type="button" className="t-code-copy" aria-label={`Copy code ${props.code}`} onClick={props.onCopy}>
        {/* A failed press does NOT wear the settled label: the code is not
            on the clipboard, so the door still offers to copy. */}
        {copied ? "Copied" : "Copy"}
      </button>
      {copied ? (
        <span role="status" className="t-visually-hidden">
          {codeCopyWords("copied")}
        </span>
      ) : null}
      {/* B306 — the failure is stated VISIBLY, because the person holding the
          empty clipboard is the one who can see the screen. It rides the package
          Banner (B213 rule (i): a refusal is not a raw alert), and an untried
          door draws nothing at all — pressing nothing is not a mistake, and the
          ELEMENT is absent rather than merely its words (B305 #65).

          THE TWO FAILURES ARE TWO SITES because their WAYS BACK differ, and a
          way back that cannot work is worse than none (B302). */}
      {props.outcome === "refused" ? (
        <span className="t-code-note">
          <Banner tone="danger">
            {codeCopyWords("refused")}{" "}
            <Button variant="neutral" onClick={props.onCopy}>
              Try again
            </Button>
          </Banner>
        </span>
      ) : null}
      {props.outcome === "unavailable" ? (
        <span className="t-code-note">
          {/* B306 — this browser hands the page no clipboard AT ALL, so a Try again
              would draw a door that can never open (B302). The way back is the code
              itself, one line above and select-all on click; the sentence names it. */}
          <Banner tone="danger">{codeCopyWords("unavailable")}</Banner>{/* wayback-ok: no clipboard exists on this page, so the way back is the selectable code above, which the sentence names */}
        </span>
      ) : null}
    </span>
  );
}

/**
 * B115 — the one identifier an operator may see: a short confirmation
 * code as a copyable chip. Never the way to FIND something (that is
 * what lists and search are for); always copyable in one press.
 */
export function ConfirmationCode(props: { code: string; clipboard?: ClipboardLike }): ReactNode {
  const [outcome, setOutcome] = useState<CodeCopyOutcome>("untried");
  return (
    <ConfirmationCodeFace
      code={props.code}
      outcome={outcome}
      onCopy={() => {
        const clip =
          props.clipboard ?? (typeof navigator === "undefined" ? undefined : (navigator.clipboard as ClipboardLike | undefined));
        void copyCodeEffect({ code: props.code, clipboard: clip }).then((next) => {
          setOutcome(next);
          if (next === "copied") setTimeout(() => setOutcome("untried"), 1600);
        });
      }}
    />
  );
}

/* ------------------------------------------------------------ WordSwap */

/** One mapping row — the neutral word and the word that replaces it. */
export interface WordSwapRow {
  readonly from: string;
  readonly to: string;
}

/**
 * B355 — the frame's word-mapping rows (settings-your-business.png,
 * WHAT THIS CHANGES): faint neutral word, arrow, strong new word. The
 * arrow is decoration (aria-hidden); a screen reader hears "a time slot
 * becomes a departure". No rows renders nothing — an empty mapping is
 * the caller's sentence to write, not an empty box to ship.
 */
export function WordSwap(props: { readonly rows: readonly WordSwapRow[] }): ReactNode {
  if (props.rows.length === 0) return null;
  return (
    <ul className="t-wordswap">
      {props.rows.map((row) => (
        <li key={`${row.from}→${row.to}`} className="t-wordswap-row">
          <span className="t-wordswap-from">{row.from}</span>
          <span aria-hidden="true" className="t-wordswap-arrow">→</span>
          <span className="t-visually-hidden">becomes</span>
          <span className="t-wordswap-to">{row.to}</span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------ Initials */

/**
 * B513 — two letters a person would recognise: the first letter of the
 * first and last word of a name; an email reads its local part split on
 * `.`, `_` and `-` (an invite with no name still gets a face). Nothing in,
 * nothing out — the caller decides what an empty face means.
 */
export function initialsOf(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  const source = trimmed.includes("@") ? (trimmed.split("@")[0] ?? "") : trimmed;
  const words = source.split(/[\s._-]+/).filter((w) => w.length > 0);
  if (words.length === 0) return "";
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

/**
 * B513 (Figma 338-2) — the roster's initials disc. DECORATIVE by
 * construction: the person's name is always drawn beside it, so the disc
 * is aria-hidden and never the only home of who a row is (B287). `muted`
 * is the switched-off face.
 */
export function Initials(props: { readonly name: string; readonly muted?: boolean }): ReactNode {
  const letters = initialsOf(props.name);
  if (letters === "") return null;
  return (
    <span className={props.muted === true ? "t-initials t-initials--muted" : "t-initials"} aria-hidden="true">
      {letters}
    </span>
  );
}
